import { AssetType } from '@lucimark/shared/models/asset-type'

export type TFilterableQueueFacet = {
  asset?: string
  type?: string
}

export type TFilterableQueueFrame = {
  facets?: Record<string, TFilterableQueueFacet> | null
}

/**
 * URNs that play from metadata (URL widget, HLS) and must not wait on a binary download.
 */
export function collectReadyWithoutBinaryUrns(
  widgetAssetMetadata: Record<string, { urlTemplate?: string | null }> | null | undefined,
  hlsVideoAssetMetadata: Record<string, { hlsUrl?: string | null }> | null | undefined
): Set<string> {
  const urns = new Set<string>()
  for (const [urn, meta] of Object.entries(widgetAssetMetadata ?? {})) {
    if (typeof meta?.urlTemplate === 'string' && meta.urlTemplate !== '') {
      urns.add(urn)
    }
  }
  for (const [urn, meta] of Object.entries(hlsVideoAssetMetadata ?? {})) {
    if (typeof meta?.hlsUrl === 'string' && meta.hlsUrl !== '') {
      urns.add(urn)
    }
  }
  return urns
}

function facetBlocksUntilCached(
  facet: TFilterableQueueFacet,
  cachedUrns: ReadonlySet<string>,
  readyWithoutBinaryUrns: ReadonlySet<string>
): boolean {
  const urn = facet.asset?.trim() ?? ''
  if (urn === '' || facet.type === AssetType.Widget || readyWithoutBinaryUrns.has(urn)) {
    return false
  }
  return !cachedUrns.has(urn)
}

/**
 * Drop frames whose image/video binary is not cached yet.
 * Widgets, HLS, and frames with no asset stay. The omitted frame returns on the next publish
 * once its URN is in `cachedUrns` — one gate before the scheduler, not per renderer.
 */
export function filterQueueToCachedMedia<T extends TFilterableQueueFrame>(
  frames: readonly T[],
  cachedUrns: ReadonlySet<string>,
  readyWithoutBinaryUrns: ReadonlySet<string>
): { playable: T[], skippedCount: number } {
  const playable: T[] = []
  let skippedCount = 0
  for (const frame of frames) {
    const facets = frame.facets == null ? [] : Object.values(frame.facets)
    const blocked = facets.some(facet =>
      facetBlocksUntilCached(facet, cachedUrns, readyWithoutBinaryUrns)
    )
    if (blocked) {
      skippedCount += 1
    }
    else {
      playable.push(frame)
    }
  }
  return { playable, skippedCount }
}
